import { useQueries, type UseQueryResult } from '@tanstack/react-query'
import { createFileRoute, useNavigate, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import SegmentedControl from '@/components/ui/SegmentedControl'
import { PLANS, type PlanPresentation } from '@/features/onboarding/plans'
import {
  planQueryOptions,
  useSuspensePlans,
  useSuspenseSubscriptionMe,
} from '@/features/subscriptions/api/queries'
import {
  buildCatalog,
  defaultPeriod,
  findPlan,
  offerFor,
  periodLabel,
  type Catalog,
} from '@/features/subscriptions/lib/catalog'
import { highlightFeatures, planSummary } from '@/features/subscriptions/lib/features'
import {
  knownTierSchema,
  type Entitlement,
  type KnownTier,
  type Plan,
  type PlanDetail,
} from '@/schemas/subscriptions'
import AccountPageHeader from '@/screens/account/components/AccountPageHeader'
import PlanOfferCard, {
  type PlanCardAction,
  type PlanCardFeatures,
  type PlanCardPrice,
} from '@/screens/account/components/PlanOfferCard'
import CurrentPlanBanner from '@/screens/account/components/plans/CurrentPlanBanner'
import {
  cardPlan,
  featurePlanIds,
  nearestPeriod,
  savingsLine,
} from '@/screens/account/components/plans/planGrid'
import PlansPending from '@/screens/account/components/plans/PlansPending'
import SubscriptionSteps from '@/screens/account/components/SubscriptionSteps'
import BillingHistoryDialog from '@/screens/account/components/manage/BillingHistoryDialog'
import { loadSubscription } from '@/screens/account/SubscriptionScreen.loader'
import { billingSearchSchema } from '@/screens/account/subscription-search'

const HEADER = <AccountPageHeader title="Subscription" />

/** How long a picked card stands selected before checkout opens (mock `su-card`, 420 ms). */
const SELECTED_BEAT_MS = 420

function PlansError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load the plans" />
}

export const Route = createFileRoute('/_app/subscription')({
  validateSearch: billingSearchSchema,
  loader: loadSubscription,
  pendingComponent: PlansPending,
  errorComponent: PlansError,
  component: SubscriptionScreen,
})

type DetailQuery = UseQueryResult<PlanDetail | null>

/** A card's tagline and ticks from its plan's flags, as the card's own load state. */
function featuresOf(
  query: DetailQuery | undefined,
  build: (detail: PlanDetail) => PlanCardFeatures,
  missing: PlanCardFeatures = { status: 'none' },
): PlanCardFeatures {
  if (query === undefined) return { status: 'none' }
  if (query.isPending) return { status: 'loading' }
  if (query.isError) {
    return {
      status: 'failed',
      retrying: query.isFetching,
      onRetry: () => {
        void query.refetch()
      },
    }
  }
  return query.data === null ? missing : build(query.data)
}

/** A paid plan whose flags could not be found reads as one with none listed. */
const NONE_LISTED: PlanCardFeatures = { status: 'ready', tagline: null, items: [] }

const ownFeatures = (detail: PlanDetail): PlanCardFeatures => ({
  status: 'ready',
  tagline: planSummary(detail.features),
  items: highlightFeatures(detail.features, 3),
})

/**
 * Guru's card says what it adds over Sadasya's plan for the same period —
 * Figma's "Depth, valuators and newsletters on top." — and only when there is
 * a Sadasya plan to be on top OF. With none for the period, or none whose
 * flags the web can name, it lists its own, with no "on top".
 */
function guruFeatures(guru: DetailQuery | undefined, sadasya: DetailQuery | undefined) {
  if (sadasya?.isPending === true) return featuresOf(guru, () => ({ status: 'loading' }))
  const except = sadasya?.data?.features
  const onTop = except !== undefined && highlightFeatures(except, Infinity).length > 0
  if (!onTop) return featuresOf(guru, ownFeatures, NONE_LISTED)
  return featuresOf(
    guru,
    (detail) => {
      const summary = planSummary(detail.features, { except })
      return {
        status: 'ready',
        tagline: summary === null ? null : summary.replace(/\.$/, ' on top.'),
        items: highlightFeatures(detail.features, 3, { except }),
      }
    },
    NONE_LISTED,
  )
}

interface CardProps {
  price: PlanCardPrice
  features: PlanCardFeatures
  action: PlanCardAction
}

function ownPrice(plans: readonly Plan[], me: Entitlement, tier: KnownTier): PlanCardPrice {
  const plan = me.planId === null ? null : findPlan(plans, me.planId)
  return plan !== null && plan.tier === tier ? { kind: 'priced', plan } : { kind: 'unlisted' }
}

function cardFor(
  presentation: PlanPresentation,
  context: {
    catalog: Catalog
    plans: readonly Plan[]
    me: Entitlement
    current: boolean
    periodDays: number | null
    details: ReadonlyMap<number, DetailQuery>
    onSwitch: (periodDays: number) => void
  },
): CardProps {
  const { catalog, plans, me, current, periodDays, details, onSwitch } = context
  const { tier } = presentation

  // Free is never sold, and `plans/` never lists it: its flags are only
  // reachable through the member's own plan id, so only a Free member's
  // card lists them.
  if (tier === 'free') {
    return {
      price: { kind: 'free' },
      features:
        current && me.planId !== null
          ? featuresOf(details.get(me.planId), ownFeatures)
          : { status: 'none' },
      action: current ? { kind: 'current' } : { kind: 'none' },
    }
  }

  // The member's own card says what THEY pay, not what the period picked
  // sells: their plan from `plans/`, or no price when it is not listed.
  const own = current ? ownPrice(plans, me, tier) : null

  const offer = periodDays === null ? null : offerFor(catalog, tier, periodDays)
  if (offer === null) {
    const switchTo = periodDays === null ? null : nearestPeriod(catalog, tier, periodDays)
    return {
      price:
        own ??
        (periodDays === null || switchTo === null
          ? ({ kind: 'offSale' } as const)
          : ({ kind: 'notSold', periodDays } as const)),
      features: { status: 'none' },
      action: current
        ? { kind: 'current' }
        : switchTo === null
          ? { kind: 'none' }
          : { kind: 'switch', periodDays: switchTo, onSwitch },
    }
  }

  const plan = cardPlan(offer)
  const sadasyaOffer = periodDays === null ? null : offerFor(catalog, 'bull', periodDays)
  return {
    price: own ?? { kind: 'priced', plan },
    features:
      tier === 'shark'
        ? guruFeatures(
            details.get(plan.id),
            sadasyaOffer === null ? undefined : details.get(cardPlan(sadasyaOffer).id),
          )
        : featuresOf(details.get(plan.id), ownFeatures, NONE_LISTED),
    action: current ? { kind: 'current' } : { kind: 'choose', planId: plan.id },
  }
}

/**
 * Subscription — pick a plan, step 1 of 3. V2 node `4243:75036`.
 *
 * Every figure on it is the API's: the cards' prices and periods from
 * `plans/`, their taglines and ticks from each plan's feature flags, the
 * banner and the "Your plan" card from `subscriptions/me/`. The period switch
 * offers the periods the catalogue sells, so on dev's catalogue it reads
 * Monthly / 3 months / Yearly, not Figma's Monthly / Annual.
 *
 * Figma draws no comparison table here. The one this page used to carry had
 * eighteen invented limits and is gone; the cards are the comparison.
 */
function SubscriptionScreen() {
  const { data: plans } = useSuspensePlans()
  const { data: me } = useSuspenseSubscriptionMe()
  const catalog = useMemo(() => buildCatalog(plans), [plans])
  const [picked, setPicked] = useState<number | null>(null)
  const periodDays =
    picked !== null && catalog.periods.includes(picked) ? picked : defaultPeriod(catalog)

  const detailIds = featurePlanIds(catalog, me)
  const detailQueries = useQueries({ queries: detailIds.map((id) => planQueryOptions(id)) })
  const details = new Map(
    detailIds.flatMap((id, index) => {
      const query = detailQueries[index]
      return query === undefined ? [] : [[id, query] as const]
    }),
  )

  const memberTier = knownTierSchema.safeParse(me.tier)
  const currentTier = memberTier.success ? memberTier.data : null
  const saving = periodDays === null ? null : savingsLine(catalog, periodDays)

  const { billing } = Route.useSearch()
  // Q9: a picked card shows its gold tick and "Selected" for a beat, then checkout opens.
  const navigate = useNavigate()
  const [chosen, setChosen] = useState<number | null>(null)
  const leave = useRef<number | undefined>(undefined)
  useEffect(
    () => () => {
      window.clearTimeout(leave.current)
    },
    [],
  )
  const choose = (planId: number) => {
    if (chosen !== null) return
    setChosen(planId)
    leave.current = window.setTimeout(() => {
      void navigate({ to: '/subscription/checkout', search: { plan: planId } })
    }, SELECTED_BEAT_MS)
  }

  return (
    <ScreenLayout header={HEADER} fullWidth>
      <SubscriptionSteps current="pick" />

      <CurrentPlanBanner me={me} />

      {periodDays !== null && catalog.periods.length > 1 && (
        // Centred, the note under the switch (mock `su-cycle`). The periods are the catalogue's
        // own: dev sells three, so this reads Monthly / 3 months / Yearly, not Monthly / Annual.
        <div className="flex flex-col items-center gap-2">
          <SegmentedControl
            ariaLabel="How often you pay"
            options={catalog.periods.map((days) => ({
              value: String(days),
              label: periodLabel(days),
            }))}
            value={String(periodDays)}
            onChange={(value) => {
              setPicked(Number(value))
            }}
          />
          <p
            aria-live="polite"
            className="min-h-4 text-center text-body-xs text-muted-foreground-subtle"
          >
            {saving}
          </p>
        </div>
      )}

      {/* Three equal columns in one row. `@3xl` (768px of track) rather than `md:`: the main
          column narrows beside the Mitra panel, and a viewport breakpoint cannot see that. Below
          it the cards stack, full width. One radio group (owner U8): a card is picked with a
          click anywhere on it, and the pick then opens checkout. */}
      <div role="radiogroup" aria-label="Plans" className="grid gap-6 pt-2 @3xl:grid-cols-3">
        {PLANS.map((presentation) => {
          const current = presentation.tier === currentTier
          return (
            <PlanOfferCard
              key={presentation.tier}
              plan={presentation}
              recommended={presentation.recommended && !current}
              chosen={chosen}
              onChoose={choose}
              {...cardFor(presentation, {
                catalog,
                plans,
                me,
                current,
                periodDays,
                details,
                onSwitch: setPicked,
              })}
            />
          )
        })}
      </div>

      {/* A Free member sent here by `/subscription/billing` (see `billingSearchSchema`). */}
      <BillingHistoryDialog
        open={billing === true}
        onOpenChange={(open) => {
          if (!open) void navigate({ to: '/subscription', search: {}, replace: true })
        }}
      />
    </ScreenLayout>
  )
}
